import axios from "axios";
import moment from "moment";
import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Alert,
  Stack,
  Breadcrumb,
} from "react-bootstrap";
import { useParams } from "react-router-dom";

import Notes from "components/Notes/Notes";
import UnauthorizedView from "components/UI/UnauthorizedView";
import { useAuth } from "hooks/useAuth";
import { useInterval } from "hooks/useInterval";

import ACMRCompletedModal from "./ACMRCompletedModal";
import ACMRRecommendationModal from "./ACMRRecommendationModal";
import AMHeader from "./AMHeader";
import AMProgressPoints from "./AMProgressPoints";
import AMSideInfo from "./AMSideInfo";
import AssuranceMaintenanceAccordion from "./AssuranceMaintenanceAccordion";

import "../ProductDetails.css";
import "../../Products.css";

export default function CheckPermAMDetails() {
  const { maintenance_id } = useParams();
  const { authToken, permissions } = useAuth();

  const [hasPerm, setHasPerm] = useState();

  useEffect(() => {
    if (maintenance_id) {
      axios
        .get(
          `${import.meta.env.VITE_DJANGO_ENDPOINT}project/maintenance/has_permissions_to_view_maintenance/?maintenance_id=${maintenance_id}`,
          {
            withCredentials: true,
            headers: { Authorization: `Token ${authToken}` },
          },
        )
        .then((response) => {
          if (response.data?.authorized === false) {
            setHasPerm(false);
          } else {
            setHasPerm(true);
          }
        })
        .catch(() => {
          setHasPerm(false);
        });
    }
  }, [maintenance_id]);

  if (
    !permissions?.role_permissions?.find(
      (permission) => permission.area === "Assurance Maintenance",
    )?.read
  ) {
    return <UnauthorizedView />;
  } else if (
    permissions?.role_permissions?.find(
      (permission) => permission.area === "Assurance Maintenance",
    )?.local_only &&
    hasPerm === false
  ) {
    return <UnauthorizedView />;
  } else if (
    permissions?.role_permissions?.find(
      (permission) => permission.area === "Assurance Maintenance",
    )?.local_only &&
    hasPerm === true
  ) {
    return <AssuranceMaintenanceDetails />;
  } else if (hasPerm === true) {
    //this would be for NIAP and Validators
    return <AssuranceMaintenanceDetails />;
  }
  return <div></div>;
}

function AssuranceMaintenanceDetails() {
  const { id, maintenance_id } = useParams();
  const [product, setProduct] = useState({});
  const [maintenance, setMaintenance] = useState({});
  const [refetchNotes, setRefetchNotes] = useState(false);
  const [amProgressPoints, setAmProgressPoints] = useState([]);
  const [alert, setAlert] = useState({});
  const [updateSidebarFiles, setUpdateSidebarFiles] = useState(0);
  const [showACMRCompletedModal, setShowACMRCompletedModal] = useState(false);
  const [showACMRModal, setShowACMRModal] = useState(false);
  const { authToken, permissions } = useAuth();

  useEffect(() => {
    document.title = "NIAP Community - Product Details";
  }, []);

  const loadAssuranceMaintenance = useCallback(() => {
    if (id) {
      axios
        .get(
          `${import.meta.env.VITE_DJANGO_ENDPOINT}project/maintenance/${maintenance_id}/`,
          {
            withCredentials: true,
            headers: { Authorization: `Token ${authToken}` },
          },
        )
        .then((response) => {
          setMaintenance(response.data);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [maintenance_id]);

  useEffect(() => {
    loadAssuranceMaintenance();
  }, [loadAssuranceMaintenance]);

  useInterval(() => {
    loadAssuranceMaintenance();
  }, 300000); //5 min

  const loadProject = useCallback(() => {
    if (id) {
      axios
        .get(`${import.meta.env.VITE_DJANGO_ENDPOINT}project/product/${id}/`, {
          withCredentials: true,
          headers: { Authorization: `Token ${authToken}` },
        })
        .then((response) => {
          setProduct(response.data);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [maintenance_id]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  useInterval(() => {
    loadProject();
  }, 300000); //5 min

  const getAmProgressPoints = useCallback(() => {
    if (maintenance_id) {
      axios
        .get(
          `${import.meta.env.VITE_DJANGO_ENDPOINT}project/progress/progress_points_by_maintenance_id/?maintenance_id=${maintenance_id}`,
          {
            withCredentials: true,
            headers: { Authorization: `Token ${authToken}` },
          },
        )
        .then((response) => {
          setAmProgressPoints(
            response.data.sort((a, b) => a.prog_id - b.prog_id),
          );
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [maintenance_id]);

  useEffect(() => {
    getAmProgressPoints();
  }, [getAmProgressPoints]);

  useInterval(() => {
    getAmProgressPoints();
  }, 300000); //5 min

  const updateFiles = () => {
    console.log("updateFiles being called");
    setUpdateSidebarFiles(updateSidebarFiles + 1);
  };

  const showAMButtons = (pp) => {
    switch (pp?.status) {
      case "Not Started":
        switch (pp?.progress_point) {
          case 3:
            if (
              amProgressPoints[1]?.status === "Completed" &&
              permissions?.role_type === "Validator"
            ) {
              return (
                <Button
                  size="sm"
                  variant="warning"
                  className="rounded-pill"
                  onClick={() => setShowACMRCompletedModal(true)}
                >
                  ACMR Completed
                </Button>
              );
            } else {
              return <></>;
            }
          default:
            return;
        }
      case "Rejected":
        switch (pp?.progress_point) {
          case 3:
            if (
              amProgressPoints[1]?.status === "Completed" &&
              permissions?.role_type === "Validator"
            ) {
              return (
                <Button
                  size="sm"
                  variant="warning"
                  className="rounded-pill"
                  onClick={() => setShowACMRCompletedModal(true)}
                >
                  Re-Upload ACMR
                </Button>
              );
            } else {
              return <></>;
            }
          default:
            return;
        }
      case "In Progress":
        switch (pp?.progress_point) {
          case 3:
            if (
              amProgressPoints[1]?.status === "Completed" &&
              permissions?.role_type === "Validator" &&
              permissions?.role_permissions?.find(
                (permission) => permission.area === "AM Approval Authority",
              )?.create
            ) {
              return (
                <Button
                  size="sm"
                  variant="warning"
                  className="rounded-pill"
                  onClick={() => setShowACMRModal(true)}
                >
                  Approve/Recommend
                </Button>
              );
            } else {
              return <></>;
            }
          default:
            return;
        }
      case "Completed":
        switch (pp?.progress_point) {
          case 1: {
            const labRequest = maintenance?.lab_request;
            const vendorRequest = maintenance?.vendor_request;
            const requestData = labRequest?.id
              ? { person: labRequest, date: pp?.maintenance?.lab_request_date }
              : vendorRequest?.id
                ? {
                    person: vendorRequest,
                    date: pp?.maintenance?.vendor_request_date,
                  }
                : null;

            return (
              <Stack>
                <small className="small-font">Requested by</small>
                {requestData && (
                  <>
                    <small className="small-font">
                      {requestData.person.first_name}{" "}
                      {requestData.person.last_name}
                    </small>
                    <small className="mb-3 small-font">
                      on {moment.utc(requestData.date).format("MM/DD/YYYY")}
                    </small>
                  </>
                )}
              </Stack>
            );
          }
          case 2:
            return (
              <Container>
                <Row>
                  <Col>
                    <Row>
                      <small className="small-font">Team Assigned</small>
                    </Row>
                    <Row>
                      <small className="mb-3 small-font">
                        on {moment(pp?.date).format("MM/DD/YYYY")}
                      </small>
                    </Row>
                  </Col>
                </Row>
              </Container>
            );
          case 3:
          case 4:
          default:
            return (
              <Stack>
                <small className="small-font">Submitted by</small>
                <small className="small-font">
                  {pp?.editor
                    ? pp?.editor?.first_name + " " + pp?.editor?.last_name
                    : pp?.creator?.first_name + " " + pp?.creator?.last_name}
                </small>
                {pp?.date && (
                  <small className="mb-3 small-font">
                    on {moment.utc(pp.date).format("MM/DD/YYYY")}
                  </small>
                )}
              </Stack>
            );
        }
      default:
        return <></>;
    }
  };

  return (
    <>
      <Container className="py-5" fluid>
        <Row>
          <Col sm={12} md={8} lg={9}>
            <Row>
              {alert.message && (
                <Alert
                  variant={alert.variant}
                  onClose={() => setAlert({})}
                  dismissible
                >
                  {alert.message}
                </Alert>
              )}
            </Row>
          </Col>
        </Row>
        <Row>
          <Breadcrumb>
            <Breadcrumb.Item
              href={`/community/products/details/${product?.product_id}`}
            >
              VID {product?.v_id} Details
            </Breadcrumb.Item>
            <Breadcrumb.Item active>{maintenance?.title}</Breadcrumb.Item>
          </Breadcrumb>
        </Row>
        <Row>
          <Col sm={12} md={8} lg={9}>
            <Row>
              <Col sm={12} className="mt-3">
                <AMHeader
                  product={product}
                  maintenance={maintenance}
                  refetch={() => {
                    loadAssuranceMaintenance();
                    getAmProgressPoints();
                    loadProject();
                  }}
                  progressPoints={amProgressPoints}
                  setAlert={setAlert}
                />
              </Col>
              <Col sm={12}>
                <h5 className="fw-bold mt-3">MILESTONES</h5>
              </Col>
              <AMProgressPoints
                progressPoints={amProgressPoints}
                showButtons={showAMButtons}
                maintenance={maintenance}
              />
              <Col sm={12} className="mt-3" id="accordion-section">
                <AssuranceMaintenanceAccordion
                  maintenance={maintenance}
                  refetch={loadAssuranceMaintenance}
                  amProgressPoints={amProgressPoints}
                />
                <Notes
                  item={maintenance}
                  note_type="assurance_maintenance"
                  id={maintenance_id}
                />
              </Col>
            </Row>
          </Col>
          <Col sm={12} md={4} lg={3}>
            <AMSideInfo
              maintenance={maintenance}
              product={product}
              updateSidebarFiles={updateSidebarFiles}
              refetch={loadAssuranceMaintenance}
            />
          </Col>
        </Row>
        <ACMRCompletedModal
          show={showACMRCompletedModal}
          handleClose={() => setShowACMRCompletedModal(false)}
          project={product}
          refetch={() => {
            updateFiles();
            getAmProgressPoints();
          }}
          setAlert={setAlert}
        />
        <ACMRRecommendationModal
          show={showACMRModal}
          setShow={setShowACMRModal}
          product={product}
          refetch={() => {
            updateFiles();
            getAmProgressPoints();
          }}
          setRefetchNotes={setRefetchNotes}
        />
      </Container>
    </>
  );
}
